import { canAccessAdminArea } from '../utils/adminAccess';
// AuthenticatedApp.tsx
import React, { Fragment, Suspense } from 'react';
import { Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
// NonIdealState replaced with inline Skyline empty states
import RecordList from './RecordList';
import LoadingDialog from './LoadingDialog';
import './AuthenticatedApp.css';
import SidebarLayout from './SidebarLayout';
import { LinkedRecordPanelProvider } from './LinkedRecordPanel';
import { SidePanelProvider } from './SidePanel/SidePanelContext';
import { FileViewerProvider } from './FileViewer';
import { useGlobalMetadata } from '../utils/providers';
import SearchResults from './SearchResults';
import useAuth from '../utils/useAuth';
import { useQuery } from '@tanstack/react-query';
import { loadRepOnlyNavigation } from '../utils/repOnlyNavigation';
import { queryPolicies } from '../utils/queryPolicies';
import { getImpersonationMeta, initImpersonationSync, stopImpersonation } from '../utils/impersonation';
import { useMyCapabilities } from '../utils/metadata/capabilities';
import { RouteTelemetryObserver } from './RouteTelemetryObserver';

/** Amber top bar shown while a super admin is impersonating another user. */
const ImpersonationBanner = (): React.ReactElement | null => {
    const meta = getImpersonationMeta();
    const [exiting, setExiting] = React.useState(false);
    const [exitError, setExitError] = React.useState<string | null>(null);

    // Mounted for every authenticated user, impersonating or not: a tab also has to
    // pick up an impersonation that another tab starts.
    React.useEffect(() => initImpersonationSync(), []);

    // stopImpersonation navigates on success, so this only resolves on failure.
    // Report it in the banner instead of leaving a button that silently does nothing.
    const onExit = (): void => {
        setExiting(true);
        setExitError(null);
        stopImpersonation()
            .catch((error: unknown) => {
                setExitError(error instanceof Error ? error.message : 'Could not exit impersonation.');
                setExiting(false);
            });
    };

    if (!meta) return null;
    return (
        <div style={{
            position: 'sticky', top: 0, zIndex: 1000, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 12, padding: '6px 16px',
            background: '#fdf6e3', borderBottom: '1px solid #f0dba4', color: '#8a5a00', fontSize: 13,
        }}>
            <span>
                Viewing as <strong>{meta.targetName || meta.targetEmail}</strong> — everything behaves as their role sees it.
            </span>
            <button
                style={{
                    border: '1px solid #f0dba4', borderRadius: 6, background: '#fff',
                    padding: '2px 10px', fontSize: 12, fontWeight: 600, color: '#8a5a00',
                    cursor: exiting ? 'default' : 'pointer',
                }}
                disabled={exiting}
                onClick={onExit}
            >
                {exiting ? 'Exiting…' : 'Exit impersonation'}
            </button>
            {exitError && <span style={{ color: '#a82a2a' }}>{exitError}</span>}
        </div>
    );
};

/** Login landing: rep-only users go to their own dashboard, everyone else to the main one. */
const HomeRedirect = (): React.ReactElement => {
    const location = useLocation();
    const { appUser } = useAuth();
    const repNavigation = useQuery({
        queryKey: ['rep-only-navigation', appUser?.id],
        queryFn: () => loadRepOnlyNavigation(appUser!.id),
        enabled: Boolean(appUser?.id),
        ...queryPolicies.permissions,
    });
    if (!appUser?.id || repNavigation.isLoading) {
        return <LoadingDialog messages={['Loading your dashboard...']} title="Loading" />;
    }
    const target = repNavigation.data?.isInstantlyPartner ? '/partner-leads'
        : repNavigation.data?.isRepOnly ? '/my-dashboard' : '/main-dashboard';
    return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

const CommandCenterRedirect = (): React.ReactElement => {
    const location = useLocation();
    return <Navigate to={`/main-dashboard${location.search}${location.hash}`} replace />;
};

const Admin = React.lazy(() => import('./admin/Admin'));
const OpportunitiesPage = React.lazy(() => import('./OpportunitiesPage/OpportunitiesPage'));
// REG-922: the Instantly partner's leads list.
const PartnerLeadsPage = React.lazy(() => import('./PartnerLeadsPage/PartnerLeadsPage'));
const ReportPage = React.lazy(() => import('./ReportPage/ReportPage'));
const ReportsIndex = React.lazy(() => import('./ReportPage/ReportsIndex'));
const ReportingAI = React.lazy(() => import('./ReportPage/ReportingAI'));
const DashboardPage = React.lazy(() => import('./DashboardPage/DashboardPage'));
const MyRepDashboard = React.lazy(() => import('./RepPage/MyRepDashboard'));
const CommissionsPage = React.lazy(() => import('./Commissions/CommissionsPage'));
const AuditLogPage = React.lazy(() => import('./AuditLogPage/AuditLogPage'));
const EmailRecordsPage = React.lazy(() => import('./EmailRecordsPage/EmailRecordsPage'));
// REG-771 ACH Charge (GoACH) — lazy so users without ACH access never download it.
const AchChargePage = React.lazy(() => import('./AchCharge/AchChargePage'));

/**
 * AuthenticatedApp component serves as the main application shell for authenticated users.
 * It manages the overall layout, navigation routing, and ensures metadata is loaded before rendering.
 * 
 * Features:
 * - Global metadata loading and validation
 * - Nested routing structure with sidebar layout
 * - Loading states during metadata initialization
 * - Fallback routes for unknown paths
 */
export const AuthenticatedApp = React.memo((): React.ReactElement => {
    const { data: metadata } = useGlobalMetadata();
    const { isAdmin, loading: authLoading } = useAuth();
    const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
    const canOpenAdmin = canAccessAdminArea(isAdmin, hasCapability);
    const canManageReports = hasCapability('manage_reports');
    const canViewAuditLog = hasCapability('view_audit_log');
    const canUseAch = hasCapability('ach_initiate') || hasCapability('ach_approve');
    const canViewEmailArchive = hasCapability('view_email_archive');
 
    // Validate that essential metadata is loaded before rendering the app
    if (!metadata || !metadata.routeData?.asList?.length || !metadata.uiComponents) {
        const loadingMessages: string[] = [
          'Retrieving routes...',
        ];
        return <LoadingDialog messages={loadingMessages} title="Loading Application" />;
    }

    return (
        <FileViewerProvider>
        <SidePanelProvider>
        <LinkedRecordPanelProvider>
        <Fragment>
            <RouteTelemetryObserver />
            <ImpersonationBanner />
            <Routes>
                <Route element={<SidebarLayout />}>
                    {/* Home route: straight to the dashboard (no blank welcome page after login) */}
                    <Route path="/" element={<HomeRedirect />} />
                    
                    {/* Opportunities pipeline board — must come before /:table to avoid capture */}
                    <Route
                        path="/opportunities"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading pipeline...']} title="Loading" />}>
                                <OpportunitiesPage />
                            </Suspense>
                        }
                    />

                    {/* REG-922 partner leads list. Before /:table to avoid capture. */}
                    <Route
                        path="/partner-leads"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading leads...']} title="Loading" />}>
                                <PartnerLeadsPage />
                            </Suspense>
                        }
                    />

                    <Route path="/exports" element={<Navigate to="/admin/exports" replace />} />

                    {/* Report pages — must come before /:table to avoid capture */}
                    <Route
                        path="/reports"
                        element={
                            authLoading || capabilitiesLoading ? (
                                <LoadingDialog messages={['Checking permissions...']} title="Loading" />
                            ) : canManageReports ? (
                                <Suspense fallback={<LoadingDialog messages={['Loading reports...']} title="Loading" />}>
                                    <ReportsIndex />
                                </Suspense>
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/reports/ai"
                        element={
                            authLoading || capabilitiesLoading ? (
                                <LoadingDialog messages={['Checking permissions...']} title="Loading" />
                            ) : canManageReports ? (
                                <Suspense fallback={<LoadingDialog messages={['Loading reporting AI...']} title="Loading" />}>
                                    <ReportingAI />
                                </Suspense>
                            ) : (
                                <Navigate to="/reports" replace />
                            )
                        }
                    />
                    <Route
                        path="/reports/:key"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading report...']} title="Loading" />}>
                                <ReportPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/*"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading report...']} title="Loading" />}>
                                <ReportPage />
                            </Suspense>
                        }
                    />

                    <Route
                        path="/ach-charge"
                        element={
                            authLoading || capabilitiesLoading ? (
                                <LoadingDialog messages={['Checking permissions...']} title="Loading ACH Charge" />
                            ) : canUseAch ? (
                                <Suspense fallback={<LoadingDialog messages={['Loading ACH...']} title="Loading ACH Charge" />}>
                                    <AchChargePage />
                                </Suspense>
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/audit-log"
                        element={
                            authLoading || capabilitiesLoading ? (
                                <LoadingDialog messages={['Checking permissions...']} title="Loading Audit Log" />
                            ) : canViewAuditLog ? (
                                <Suspense fallback={<LoadingDialog messages={['Loading activity...']} title="Loading Audit Log" />}>
                                    <AuditLogPage />
                                </Suspense>
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/email-records"
                        element={
                            authLoading || capabilitiesLoading ? (
                                <LoadingDialog messages={['Checking permissions...']} title="Loading Email Records" />
                            ) : canViewEmailArchive ? (
                                <Suspense fallback={<LoadingDialog messages={['Loading emails...']} title="Loading Email Records" />}>
                                    <EmailRecordsPage />
                                </Suspense>
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />

                    {/* Dashboard pages — must come before /:table to avoid capture */}
                    <Route
                        path="/dashboard/:key"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading dashboard...']} title="Loading" />}>
                                <DashboardPage />
                            </Suspense>
                        }
                    />
                    <Route path="/command-center" element={<CommandCenterRedirect />} />
                    <Route
                        path="/main-dashboard"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading Dashboard...']} title="Loading" />}>
                                <DashboardPage />
                            </Suspense>
                        }
                    />
                    {/* Commissions: Friday payout list, receivables, payment matches (REG-743).
                        Must come before /:table to avoid capture. */}
                    <Route
                        path="/commissions/:tab?"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading commissions...']} title="Loading" />}>
                                <CommissionsPage />
                            </Suspense>
                        }
                    />
                    {/* Personal dashboard for rep-only users (REG-641). */}
                    <Route
                        path="/my-dashboard"
                        element={
                            <Suspense fallback={<LoadingDialog messages={['Loading Dashboard...']} title="Loading" />}>
                                <MyRepDashboard />
                            </Suspense>
                        }
                    />

                    {/* Dynamic routes for record operations */}
                    <Route path="/:table" element={<RecordList />} />
                    
                    {/* Admin tools share the panel-access rule. Each child route
                        checks its own capability; data access stays protected by RLS. */}
                    <Route
                        path="/admin/*"
                        element={
                            authLoading || capabilitiesLoading ? (
                                <LoadingDialog messages={['Checking permissions...']} title="Loading Admin" />
                            ) : canOpenAdmin ? (
                                <Suspense fallback={<LoadingDialog messages={['Loading admin tools...']} title="Loading Admin" />}>
                                    <Admin />
                                </Suspense>
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    
                    {/* Search functionality */}
                    <Route path="/search" element={<SearchResults />} />
                    
                    {/* Catch-all route for 404 */}
                    <Route
                        path="*"
                        element={
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 8, padding: '48px 16px', color: 'var(--text-2)' }}>
                                <div style={{ fontSize: 32, opacity: 0.2 }}>&#128269;</div>
                                <div style={{ fontSize: 14, fontWeight: 600 }}>Page Not Found</div>
                                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>The requested page does not exist or may have been moved.</div>
                            </div>
                        }
                    />
                </Route>
            </Routes>
            <Outlet />
        </Fragment>
        </LinkedRecordPanelProvider>
        </SidePanelProvider>
        </FileViewerProvider>
    );
});

AuthenticatedApp.displayName = 'AuthenticatedApp';

export default AuthenticatedApp;
